import { ClassValue } from 'clsx';
import { useId } from 'react';
import { LinkActionButtons } from '@shared/components/LinkActionButtons';
import { Menu } from '@shared/components/Menu';
import { Text } from '@shared/components/Text';
import { useToast } from '@shared/components/Toast';
import { View } from '@shared/components/View';
import { Link } from '@shared/components/link';
import { EmailField } from '@shared/data/entity/fields/EmailField';
import { getAnchorName } from '@shared/styles';
import { copyToClipboard } from '@shared/util/clipboard';
import { useIsDesktop } from '@shared/util/hooks';
import { i18n } from '@shared/util/i18n';
import { openLink } from '@shared/util/link';
import { CollectionViewFieldContainer } from './CollectionViewFieldContainer';
import { FieldVariant } from './FieldVariant';
import { useLongPressMenu } from './useLongPressMenu';
import { getEmailText } from './util/getEmailText';
import styles from './EmailCollectionViewField.module.css';

interface Props {
  variant: FieldVariant;
  className?: ClassValue;
  field: EmailField;
}

/**
 * Default and compact: every address as one mailto link, showing the first, truncating, and a
 * "+N" for the rest; on desktop a copy button sits beside it, and on a touch device a long press
 * opens a menu to email or copy them all. Inline: its text.
 */
export function EmailCollectionViewField({ field, variant, className }: Props) {
  if (field.isEmpty) {
    return null;
  }

  const isInline = variant === 'inline';
  const isCompact = variant === 'compact';
  const isDefault = variant === 'default';

  return (
    <CollectionViewFieldContainer
      fieldType={field.fieldType}
      variant={variant}
      className={[styles.emailCollectionViewField, className]}
    >
      {isInline && <InlineEmail field={field} />}
      {isCompact && <DefaultEmail field={field} />}
      {isDefault && <DefaultEmail field={field} />}
    </CollectionViewFieldContainer>
  );
}

function InlineEmail({ field }: { field: EmailField }) {
  return <>{getEmailText(field)}</>;
}

function DefaultEmail({ field }: { field: EmailField }) {
  const isDesktop = useIsDesktop();
  const { isMenuOpen, onMenuOpenChange, pressProps } = useLongPressMenu(!isDesktop);
  const anchorName = getAnchorName('email-collection-view-field', useId());
  const toast = useToast();
  const [firstEmail] = field.emails;
  const allEmails = field.emails.join(', ');
  const href = `mailto:${field.emails.join(',')}`;
  const moreCount = field.emails.length - 1;
  const copyEmail = () => {
    void copyToClipboard(allEmails);
    toast({ message: i18n('card.blocks.subtableInput.emailCopied') });
  };

  return (
    <View
      row
      centerVertically
      {...pressProps}
      data-long-press={!isDesktop || undefined}
      style={{ anchorName }}
      className={styles.row}
    >
      {/* On the View because Link doesn't pass extra props to its anchor. */}
      <View centerVertically className={styles.link} tooltip={isDesktop && allEmails}>
        <Link href={href}>
          <View row className={styles.emailText}>
            <Text numberOfLines={1}>{firstEmail}</Text>
            {moreCount > 0 && <Text className={styles.more}>{`+${moreCount}`}</Text>}
          </View>
        </Link>
      </View>
      {isDesktop && (
        <View paddingLeft={1}>
          <LinkActionButtons value={allEmails} type="email" showOpen={false} />
        </View>
      )}
      {/* Touch only: a long press opens it, in place of the desktop copy button. */}
      {!isDesktop && (
        <Menu
          isOpen={isMenuOpen}
          onOpenChange={onMenuOpenChange}
          anchorName={anchorName}
          // Its end lines up with the link's, which ends at the row's edge in a trailing slot.
          align="left"
        >
          <Menu.Content>
            <Menu.Note icon={false}>{allEmails}</Menu.Note>
            <Menu.Item
              label={i18n('card.blocks.subtableInput.sendEmail')}
              onClick={() => openLink(href)}
            />
            <Menu.Item label={i18n('card.blocks.subtableInput.copyEmail')} onClick={copyEmail} />
          </Menu.Content>
        </Menu>
      )}
    </View>
  );
}
